import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaFire,
  FaHeart,
  FaSearch,
  FaShoppingCart,
  FaTimes,
} from "react-icons/fa";
import { FaRegCircleUser } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../assest/logo.png";
import SummaryApi from "../common";
import ROLE from "../common/role";
import {
  APP_SHORT_NAME,
  NAV_ABOUT,
  NAV_CATEGORIES,
  NAV_HOT_SALE,
  ROUTES,
  SEARCH_LOADING_TEXT,
  SEARCH_PLACEHOLDER,
} from "../constants";
import Context from "../context";
import displayNPRCurrency from "../helpers/displayCurrency";
import { request } from "../helpers/request";
import { clearUserData } from "../store/userSlice";

const Header = () => {
  const user = useSelector((state) => state?.user?.user);
  const dispatch = useDispatch();
  const [menuDisplay, setMenuDisplay] = useState(false);
  const [categoryDisplay, setCategoryDisplay] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const context = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const URLSearch = new URLSearchParams(location?.search);
  const searchQuery = URLSearch.get("q") || "";
  const [search, setSearch] = useState(searchQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const searchRef = useRef(null);

  const fetchCategories = async () => {
    try {
      const response = await request(SummaryApi.categoryProduct);
      const dataResponse = await response.json();
      if (dataResponse.success) setCategories(dataResponse.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
    setMenuDisplay(false);
    setCategoryDisplay(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    try {
      const response = await request(SummaryApi.logout_user);
      const data = await response.json();
      if (data.success) {
        dispatch(clearUserData());
        // Auth cookies are HttpOnly and should be cleared by backend logout endpoint.
        toast.success(data.message);
        navigate(ROUTES.HOME);
      } else if (data.error) {
        toast.error(data.message);
      }
    } catch {
      toast.error("Logout failed. Please try again.");
    }
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (search.trim().length > 1) {
        setIsSearching(true);
        try {
          const response = await request({
            url: `${SummaryApi.searchProduct.url}?q=${encodeURIComponent(search)}&limit=6`,
            method: SummaryApi.searchProduct.method,
          });
          const data = await response.json();
          if (data.success) setSuggestions(data.data);
        } catch {
          // silent
        } finally {
          setIsSearching(false);
        }
      } else {
        setSuggestions([]);
      }
    };
    const t = setTimeout(() => {
      if (search.trim()) fetchSuggestions();
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setShowSuggestions(true);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter" && search.trim()) {
      navigate(`${ROUTES.SEARCH}?q=${encodeURIComponent(search)}`);
      setShowSuggestions(false);
    }
  };

  return (
    <header
      className={`h-16 fixed w-full top-0 z-[100] transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-ds-outline-variant/30 shadow-md"
          : "bg-white border-b border-ds-outline-variant/20"
      }`}
    >
      <div className="h-full flex items-center justify-between px-10 w-full max-w-[1280px] mx-auto">
        {/* ── Logo ───────────────────────────────────────── */}
        <Link
          to={ROUTES.HOME}
          className="flex items-center gap-2.5 group flex-shrink-0"
        >
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
            <img
              src={logo}
              alt={APP_SHORT_NAME}
              className="w-full h-full object-contain"
            />
          </div>
          <span className="hidden xs:block font-barlow font-extrabold text-[22px] tracking-tighter text-ds-on-background group-hover:text-ds-primary transition-colors duration-300 uppercase">
            {APP_SHORT_NAME}
          </span>
        </Link>

        {/* ── Desktop Nav ─────────────────────────────────── */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {/* Categories Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setCategoryDisplay(true)}
            onMouseLeave={() => setCategoryDisplay(false)}
          >
            <button className="flex items-center gap-1 font-inter font-semibold text-sm text-ds-on-background hover:text-ds-primary transition-colors duration-200 py-1">
              {NAV_CATEGORIES}
              <FaChevronDown
                className={`text-[9px] ml-0.5 transition-transform duration-300 ${categoryDisplay ? "rotate-180 text-ds-primary" : ""}`}
              />
            </button>

            <AnimatePresence>
              {categoryDisplay && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute top-full left-0 w-[580px] bg-white rounded-2xl shadow-float border border-surface-border p-4 mt-3 grid grid-cols-3 gap-1"
                >
                  {categories.map((cat) => (
                    <Link
                      key={cat.category}
                      to={`${ROUTES.PRODUCT_CATEGORY}?category=${cat.category}`}
                      className="group/cat flex items-center gap-3 p-3 rounded-xl hover:bg-surface-muted transition-all duration-200"
                      onClick={() => setCategoryDisplay(false)}
                    >
                      <div className="w-8 h-8 bg-surface-muted rounded-lg flex items-center justify-center flex-shrink-0 group-hover/cat:bg-accent group-hover/cat:text-white transition-all duration-200 overflow-hidden">
                        <img
                          src={cat.productImage?.[0]}
                          alt=""
                          className="w-full h-full object-contain mix-blend-multiply group-hover/cat:mix-blend-normal"
                        />
                      </div>
                      <span className="text-sm font-semibold capitalize text-primary group-hover/cat:text-accent transition-colors truncate">
                        {cat.category}
                      </span>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            to={ROUTES.HOT_SALE}
            className="flex items-center gap-1.5 font-semibold text-sm text-red-600 hover:text-red-700 transition-colors"
          >
            <FaFire className="text-xs" />
            {NAV_HOT_SALE}
          </Link>

          <Link
            to={ROUTES.ABOUT}
            className="font-semibold text-sm text-primary hover:text-accent transition-colors"
          >
            {NAV_ABOUT}
          </Link>
        </nav>

        {/* ── Search Bar (Desktop) ─────────────────────────── */}
        <div
          className="hidden md:block flex-grow max-w-sm xl:max-w-md mx-4 xl:mx-8 relative"
          ref={searchRef}
        >
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 text-xs pointer-events-none" />
          <input
            type="text"
            placeholder={SEARCH_PLACEHOLDER}
            value={search}
            onChange={handleSearch}
            onKeyDown={handleSearchSubmit}
            onFocus={() => setShowSuggestions(true)}
            className="w-full h-10 pl-10 pr-4 rounded-full bg-[#F5F5F5] border border-transparent focus:border-ds-primary focus:ring-1 focus:ring-ds-primary focus:bg-white outline-none text-sm font-inter text-ds-on-background placeholder:text-ds-secondary transition-all duration-200"
          />

          {/* Suggestions */}
          <AnimatePresence>
            {showSuggestions && search.trim().length > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.18 }}
                className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-float border border-surface-border overflow-hidden z-[110]"
              >
                {isSearching ? (
                  <div className="p-4 text-center text-xs font-semibold text-neutral-400 animate-pulse-soft">
                    {SEARCH_LOADING_TEXT}
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="p-1.5">
                    {suggestions.map((product) => (
                      <Link
                        key={product._id}
                        to={`/product/${product._id}`}
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-center gap-3 p-2.5 hover:bg-surface-muted rounded-lg transition-all group/sug"
                      >
                        <div className="w-10 h-10 bg-surface-muted rounded-lg border border-surface-border p-1.5 flex-shrink-0">
                          <img
                            src={product.productImage[0]}
                            alt=""
                            className="w-full h-full object-contain mix-blend-multiply"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-primary truncate group-hover/sug:text-accent transition-colors">
                            {product.productName}
                          </p>
                          <p className="text-2xs text-neutral-400 font-semibold uppercase tracking-wider">
                            {product.category}
                          </p>
                        </div>
                        <span className="text-sm font-black text-primary flex-shrink-0">
                          {displayNPRCurrency(product.sellingPrice)}
                        </span>
                      </Link>
                    ))}
                    <button
                      onClick={() => {
                        navigate(
                          `${ROUTES.SEARCH}?q=${encodeURIComponent(search)}`,
                        );
                        setShowSuggestions(false);
                      }}
                      className="w-full py-2.5 text-2xs font-black uppercase tracking-widest text-accent hover:bg-accent/5 transition-all mt-1 border-t border-surface-border rounded-b-lg"
                    >
                      See all results for "{search}"
                    </button>
                  </div>
                ) : (
                  <div className="p-5 text-center">
                    <p className="text-sm font-semibold text-primary">
                      No results found
                    </p>
                    <p className="text-2xs text-neutral-400 mt-1">
                      Try a different search term
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Right Actions ─────────────────────────────────── */}
        <div className="flex items-center gap-1 md:gap-2">
          {/* Mobile Search */}
          <button
            onClick={() => setMobileSearchOpen(true)}
            aria-label="Open search"
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-ds-on-background hover:text-ds-primary transition-all active:scale-90"
          >
            <FaSearch className="text-base" />
          </button>

          {/* Wishlist */}
          <Link
            to={ROUTES.WISHLIST}
            aria-label="Wishlist"
            className="hidden sm:flex w-9 h-9 items-center justify-center rounded-lg text-ds-on-background hover:text-ds-primary transition-all active:scale-90"
          >
            <FaHeart className="text-base" />
          </Link>

          {/* Cart */}
          <Link
            to={ROUTES.CART}
            aria-label="Cart"
            className="relative w-9 h-9 flex items-center justify-center rounded-lg text-ds-on-background hover:text-ds-primary transition-all active:scale-90"
          >
            <FaShoppingCart className="text-base" />
            {context?.cartProductCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-ds-primary text-white text-[9px] font-black min-w-[16px] h-4 rounded-full flex items-center justify-center px-1 border-2 border-white shadow-sm">
                {context.cartProductCount > 99
                  ? "99+"
                  : context.cartProductCount}
              </span>
            )}
          </Link>

          {/* User */}
          <div className="relative ml-1">
            {user?._id ? (
              <>
                <button
                  onClick={() => setMenuDisplay((p) => !p)}
                  aria-label="User menu"
                  aria-expanded={menuDisplay}
                  className="w-9 h-9 rounded-full overflow-hidden border-2 border-surface-border hover:border-accent transition-all active:scale-95 focus-visible:outline-none"
                >
                  {user?.profilePic ? (
                    <img
                      src={user.profilePic}
                      className="w-full h-full object-cover"
                      alt={user.name}
                    />
                  ) : (
                    <div className="w-full h-full bg-surface-muted flex items-center justify-center text-neutral-400">
                      <FaRegCircleUser className="text-lg" />
                    </div>
                  )}
                </button>

                <AnimatePresence>
                  {menuDisplay && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 8 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 8 }}
                      transition={{ duration: 0.18 }}
                      className="absolute top-full right-0 mt-3 w-56 bg-white rounded-2xl shadow-float border border-surface-border overflow-hidden"
                    >
                      {/* User info */}
                      <div className="px-4 py-3 bg-surface-muted flex items-center gap-3 border-b border-surface-border">
                        <div className="w-9 h-9 rounded-full overflow-hidden border border-surface-border flex-shrink-0">
                          {user?.profilePic ? (
                            <img
                              src={user.profilePic}
                              className="w-full h-full object-cover"
                              alt=""
                            />
                          ) : (
                            <div className="w-full h-full bg-white flex items-center justify-center text-neutral-300">
                              <FaRegCircleUser className="text-lg" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-2xs text-neutral-400 font-black uppercase tracking-wider">
                            Welcome
                          </p>
                          <p className="text-sm font-bold text-primary truncate">
                            {user?.name}
                          </p>
                        </div>
                      </div>

                      <nav className="p-1.5">
                        {user?.role === ROLE.ADMIN && (
                          <Link
                            to="/admin-panel/dashboard"
                            className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-primary hover:bg-surface-muted rounded-lg transition-all group"
                            onClick={() => setMenuDisplay(false)}
                          >
                            Admin Dashboard
                            <FaChevronRight className="text-xs text-neutral-300 group-hover:text-accent transition-colors" />
                          </Link>
                        )}
                        <Link
                          to={ROUTES.ACCOUNT}
                          className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-primary hover:bg-surface-muted rounded-lg transition-all group"
                          onClick={() => setMenuDisplay(false)}
                        >
                          My Profile
                          <FaChevronRight className="text-xs text-neutral-300 group-hover:text-accent transition-colors" />
                        </Link>
                        <Link
                          to={ROUTES.ORDER}
                          className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-primary hover:bg-surface-muted rounded-lg transition-all group"
                          onClick={() => setMenuDisplay(false)}
                        >
                          My Orders
                          <FaChevronRight className="text-xs text-neutral-300 group-hover:text-accent transition-colors" />
                        </Link>
                        <Link
                          to="/update-password"
                          className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-primary hover:bg-surface-muted rounded-lg transition-all group"
                          onClick={() => setMenuDisplay(false)}
                        >
                          Settings
                          <FaChevronRight className="text-xs text-neutral-300 group-hover:text-accent transition-colors" />
                        </Link>
                        <div className="h-px bg-surface-border mx-2 my-1" />
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center px-3 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 rounded-lg text-left transition-all"
                        >
                          Sign Out
                        </button>
                      </nav>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              <Link
                to={ROUTES.LOGIN}
                className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-xs font-bold rounded-lg hover:bg-accent transition-all duration-300 active:scale-95"
              >
                <FaRegCircleUser className="text-sm" />
                Sign In
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile Search Overlay ──────────────────────────── */}
      <AnimatePresence>
        {mobileSearchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute inset-0 bg-white z-[101] flex items-center px-4 gap-3"
          >
            <div className="flex-grow relative h-10">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-xs" />
              <input
                autoFocus
                type="text"
                placeholder={SEARCH_PLACEHOLDER}
                className="w-full h-full pl-9 pr-4 bg-surface-muted border border-surface-border rounded-xl outline-none text-sm font-medium focus:border-neutral-300 transition-colors"
                value={search}
                onChange={handleSearch}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && search.trim()) {
                    navigate(
                      `${ROUTES.SEARCH}?q=${encodeURIComponent(search)}`,
                    );
                    setMobileSearchOpen(false);
                  }
                }}
              />
            </div>
            <button
              onClick={() => setMobileSearchOpen(false)}
              className="text-sm font-bold text-accent hover:text-accent-hover transition-colors flex-shrink-0"
            >
              Cancel
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Mobile Menu Drawer ─────────────────────────────── */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110]"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              className="fixed inset-y-0 left-0 w-full max-w-xs bg-white z-[120] shadow-float flex flex-col"
            >
              {/* Drawer Header */}
              <div className="p-5 border-b border-surface-border flex items-center justify-between">
                <Link
                  to={ROUTES.HOME}
                  className="flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <img src={logo} className="w-8 h-8 object-contain" alt="" />
                  <span className="font-black text-xl tracking-tight text-primary">
                    {APP_SHORT_NAME}
                  </span>
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-surface-muted text-neutral-500 hover:bg-surface-hover transition-all"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Drawer Body */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6">
                {/* Quick Links */}
                <div>
                  <p className="tag text-neutral-400 mb-2 ml-1">Quick Links</p>
                  <nav className="flex flex-col gap-1">
                    <Link
                      to={ROUTES.HOT_SALE}
                      className="flex items-center justify-between p-3 bg-red-50 rounded-xl text-red-600 font-bold text-sm"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="flex items-center gap-2">
                        <FaFire /> Hot Sale
                      </span>
                      <FaChevronRight className="text-xs opacity-50" />
                    </Link>
                    <Link
                      to={ROUTES.PRODUCT_CATEGORY}
                      className="flex items-center justify-between p-3 bg-surface-muted rounded-xl text-primary font-semibold text-sm"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      All Products
                      <FaChevronRight className="text-xs opacity-40" />
                    </Link>
                  </nav>
                </div>

                {/* Categories */}
                {categories.length > 0 && (
                  <div>
                    <p className="tag text-neutral-400 mb-2 ml-1">Categories</p>
                    <div className="flex flex-col gap-0.5">
                      {categories.map((cat) => (
                        <Link
                          key={cat.category}
                          to={`${ROUTES.PRODUCT_CATEGORY}?category=${cat.category}`}
                          className="flex items-center justify-between p-3 rounded-xl text-primary font-semibold text-sm hover:bg-surface-muted transition-colors capitalize"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {cat.category}
                          <FaChevronRight className="text-[10px] text-neutral-300" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* More Links */}
                <div className="pt-4 border-t border-surface-border">
                  <Link
                    to={ROUTES.ABOUT}
                    className="block p-3 text-primary font-semibold text-sm hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    About Shalimar
                  </Link>
                  <Link
                    to={ROUTES.PRIVACY_POLICY}
                    className="block p-3 text-primary font-semibold text-sm hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Privacy Policy
                  </Link>
                </div>
              </div>

              {/* Drawer Footer */}
              {!user?._id && (
                <div className="p-4 border-t border-surface-border">
                  <Link
                    to={ROUTES.LOGIN}
                    className="flex items-center justify-center w-full h-12 bg-primary text-white font-bold text-sm rounded-xl hover:bg-accent transition-all duration-300 shadow-button-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In to Account
                  </Link>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
